import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'Snap! Raise takes a cut of what athletes raise. Sport Fund is free for athletes — they keep 100% of every donation, with recurring support paid straight to the family. An honest 2026 comparison.';

const FAQS = [
  {
    q: 'Does Sport Fund really take 0% from athletes?',
    a: "Yes. Sport Fund never deducts a fee from what an athlete raises — the athlete keeps 100% of every donation. A small fee (about 5% on bank transfers, ~7% on cards) is added on top and paid by the supporter at checkout, shown before they complete the gift.",
  },
  {
    q: 'How is Sport Fund different from Snap! Raise?',
    a: "Snap! Raise is built around team and booster campaigns and is widely reported to keep roughly 20% of what's raised — sometimes more if participation goals aren't met. Sport Fund is built for the individual athlete: the athlete owns their own page, keeps 100% of every donation, and payouts go straight to the family's bank account.",
  },
  {
    q: 'Can supporters give monthly instead of a one-time gift?',
    a: "Yes — on Sport Fund it's built in by default at no extra charge. Snap! Raise remains built around one-off team campaigns rather than ongoing giving. GoFundMe fundraisers now allow monthly giving by default alongside one-time gifts, but the supporter pays an added 5% fee just for choosing monthly, on top of standard processing.",
  },
  {
    q: 'Is Sport Fund only for teams, or can individual athletes use it?',
    a: "Sport Fund is built specifically for individual athletes and their families — not clubs, teams, or booster programs. Each athlete creates and owns their own page to share with their own personal network.",
  },
  {
    q: 'How is Sport Fund different from GoFundMe?',
    a: "GoFundMe doesn't charge a platform fee but prompts supporters to add a tip, and it's built for general crowdfunding rather than athlete fundraising specifically. GoFundMe fundraisers can now accept recurring donations by default (the organizer can restrict a fundraiser to one-time only), but it carries an extra 5% fee just for the recurring option, paid by the supporter. Sport Fund is purpose-built for athletes, with a transparent supporter-side fee (no tip prompt, no separate recurring surcharge) and recurring monthly giving built in from day one.",
  },
];

const SnapRaiseAlternatives: React.FC = () => {
  usePageMeta('Snap! Raise Alternatives (2026)', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/team-huddle.jpg',
    breadcrumbs: [{ name: 'Resources', path: '/resources' }, { name: 'Snap! Raise Alternatives' }],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });
  const TOC_SECTIONS = [
    {
      id: "why-the-percentage-cut-bothers-people",
      text: "Why the percentage cut bothers people"
    },
    {
      id: "how-sport-fund-is-different",
      text: "How Sport Fund is different"
    },
    {
      id: "an-honest-comparison",
      text: "An honest comparison"
    },
    {
      id: "a-note-for-coaches-and-booster-clubs",
      text: "A note for coaches and booster clubs"
    },
    {
      id: "who-should-consider-sport-fund",
      text: "Who should consider Sport Fund"
    },
    {
      id: "start-in-minutes",
      text: "Start in minutes"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">Snap! Raise alternatives in 2026: how athletes can keep 100%</h1>
      <p className="text-gray-500 mb-8 text-lg">If the platform's cut has ever bugged you, here's the honest comparison — and where Sport Fund fits.</p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          If you're an athlete, a parent, or a coach, you've probably used — or been pitched — Snap! Raise. It works, and it's popular. But the most common complaint, year after year, is the same one: <strong>the cut.</strong> Multiple independent reviews report that Snap keeps roughly <strong>20%</strong> of what's raised (you keep about 80%), and that the effective fee can climb toward ~30% when participation goals aren't met. Snap doesn't publish a flat public rate, so treat these as widely-reported figures — confirm the numbers in your own agreement.
        </p>
        <p>
          This page lays out the honest trade-offs, and where a different approach — Sport Fund — fits.
        </p>

        <h2 id="why-the-percentage-cut-bothers-people">Why the percentage cut bothers people</h2>
        <p>
          When a grandparent gives $100, they want $100 to reach the athlete — not $75. A platform cut quietly lowers what every supporter's gift is worth. Once supporters notice, some give less, or hesitate the next time. The money that's lost isn't abstract: it's the entry fee, the hotel night, the team meal it could have covered.
        </p>

        <h2 id="how-sport-fund-is-different">How Sport Fund is different</h2>
        <p>
          Sport Fund is built for the <strong>individual athlete</strong>, not the team treasury. The athlete (or their family) owns the page, supporters are the athlete's own friends and family, and payouts go straight to the family's bank. Four things set it apart:
        </p>
        <ul>
          <li><strong>Free for athletes — the full donation reaches them.</strong> The fee isn't taken out of what's raised. A small fee (about <strong>5% on bank transfers, ~7% on cards</strong>) is added <em>on top</em> and paid by the supporter at checkout, so the athlete receives 100% of the donation amount. It's shown before checkout — transparent, not a hidden skim.</li>
          <li><strong>Recurring-first.</strong> Supporters can give monthly, not just once — so a season's costs (travel, lodging, meals, dues, the banquet) are covered steadily instead of in one frantic push.</li>
          <li><strong>Paid straight to the family's bank</strong> via Stripe.</li>
          <li><strong>Per-athlete, owned by the athlete.</strong> Each athlete has their own page to share with their own network — the people most likely to give — rather than one team campaign.</li>
        </ul>

        <h2 id="an-honest-comparison">An honest comparison</h2>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Snap! Raise</th>
                <th>GoFundMe</th>
                <th>Sport Fund</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Cost on what's raised</strong></td>
                <td>~20% kept by the platform (you keep ~80%); widely reported to rise toward ~30% if goals aren't met</td>
                <td>0% platform fee + payment processing; supporters prompted to tip</td>
                <td><strong>$0 to the athlete; ~5% (bank) / ~7% (card) added on top for the supporter — the full donation reaches the athlete</strong></td>
              </tr>
              <tr>
                <td><strong>Recurring monthly support</strong></td>
                <td>Not supported — one-time only</td>
                <td>On by default per fundraiser since 2026 (organizer can restrict to one-time only); +5% donor fee for choosing it</td>
                <td><strong>Built-in, recurring-first, no extra fee</strong></td>
              </tr>
              <tr>
                <td><strong>Who it's built for</strong></td>
                <td>Teams / booster campaigns</td>
                <td>General crowdfunding</td>
                <td><strong>Individual athletes &amp; their families</strong></td>
              </tr>
              <tr>
                <td><strong>Payout</strong></td>
                <td>To the program</td>
                <td>To the organizer</td>
                <td><strong>Straight to the athlete/family bank</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500">
          Figures for other platforms cite publicly reported 2026 reviews and may vary by agreement; confirm current terms with each provider. Sport Fund is not affiliated with, endorsed by, or sponsored by Snap! Raise or GoFundMe. Names are used only to compare services (nominative reference).
        </p>

        <h2 id="a-note-for-coaches-and-booster-clubs">A note for coaches and booster clubs</h2>
        <p>
          Sport Fund isn't a team-budget tool, and that's deliberate. The best thing a coach or booster club can do is <strong>point athletes to it so each one starts their own free page</strong> — the athlete owns it, the family gets paid, and the program carries no payment or tax exposure. One recommendation can put a whole roster on a footing where every athlete keeps 100% of what their own community gives.
        </p>

        <h2 id="who-should-consider-sport-fund">Who should consider Sport Fund</h2>
        <p>
          Individual athletes and families across 30+ sports who want ongoing support from the people who already believe in them — without a chunk taken off the top.
        </p>

        <h2 id="start-in-minutes">Start in minutes</h2>
        <p>
          Setting up a fund page is free and takes a few minutes. Tell your story, set a target, and share it with your network.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">Start a free fund page</Link> — keep 100% of every donation.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/smart-fundraising-guide" className="text-blue-600">Smart Fundraising: Strategies That Actually Work for Athletes</Link> — set targets, write updates that get shared, and build momentum across a season.
        </p>
        <p>
          <Link to="/resources/drive-sports-and-team-fundraisers" className="text-blue-600">Drive Sports & Team Fundraising Apps: Where They Leave Individual Athletes Out</Link> — how team-drive platforms differ from an athlete-owned page.
        </p>
        <p>
          <Link to="/resources/spotfund-vs-sport-fund" className="text-blue-600">Sport Fund vs Spotfund NIL: Not the Same Platform</Link> — clearing up the name confusion with a similarly-named NIL platform.
        </p>
        <ArticleFeedback articleSlug="snap-raise-alternatives" />
      </article>
    </div>
  );
};

export default SnapRaiseAlternatives;
